<template>
  <transition name="fade" :duration="{ enter: 600, leave: 300 }">
    <VueDraggableResizable
      v-if="isVisible || forcePopup"
      :w="width"
      :h="height"
      :x="position.x"
      :y="position.y"
      :draggable="isDragging"
      :resizable="true"
      style="position: absolute; z-index: 9999"
      @dragstop="onDragStop"
      @resizestop="onResizeStop"
    >
      <div class="dialog-container">
        <div class="dialog">
          <ChatHeader
            :title="strategy.getStrategyTitle()"
            :description="chatDescription"
            @close="closeChatBox"
            @mousedown.native="startDrag"
            @mouseup.native="stopDrag"
          />

          <ChatBody
            :messages="messages"
            :style="{ opacity: showLoading ? '0.3' : '1' }"
          />

          <SpinningWheel v-if="showLoading" />
          <LoadingBar v-if="showLoading" :progressValue="loadingProgress" />

          <ChatFooter
            @send="addMessage"
            @regenerate="regenerateSummary"
            :showRegenerate="true"
            :disableRegenerate="this.showLoading"
          />
        </div>
      </div>
    </VueDraggableResizable>
  </transition>
</template>

<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import VueDraggableResizable from 'vue-draggable-resizable';
import { v4 as uuidv4 } from 'uuid';
import ChatHeader from './components/ChatHeader';
import ChatBody from './components/ChatBody';
import ChatFooter from './components/ChatFooter';
import SpinningWheel from './components/SpinningWheel';
import LoadingBar from './components/LoadingBar';

import amplifyEventService from '../../services/aws-amplify/amplifyEvent';
import { SEND_REPAIR_SELECT_REQUEST } from '../../store/repairselect/actions/actionTypes';
import { REPAIR_SELECT_REQUEST } from '../../store/jobs/actions/actionTypes';

export default {
  components: {
    ChatHeader,
    ChatBody,
    ChatFooter,
    VueDraggableResizable,
    SpinningWheel,
    LoadingBar,
  },
  props: {
    isVisible: Boolean,
    title: String,
    message: String,
    chatboxMetadata: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    strategy: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      isDragging: false,
      position: { x: window.innerWidth - 100, y: window.innerHeight - 720 },
      width: 480,
      height: 600,
      messages: [],
      showLoading: false,
      loadingProgress: 0,
      channel: null,
      chatId: uuidv4(),
      chatPreviousMessage: null,
      forcePopup: false,
      interactions: [],
      summaryAlreadyRequested: false,
    };
  },
  watch: {
    $props: {
      deep: true,
      immediate: true,
      handler({ strategy }) {
        if (!strategy || typeof strategy.getIdentity !== 'function') return;

        if (this.isVisible) {
          this.handleVisibilityChange();
        }
      },
    },
    message(newMessage) {
      this.messages = [{ text: newMessage, isUser: false }];
    },
  },
  async mounted() {
    this.channel = await amplifyEventService.connectToChannel('/dij/*');
    this.setupChannelSubscription();
  },
  computed: {
    chatDescription() {
      return this.strategy.getStrategyDescription
        ? this.strategy.getStrategyDescription(this.chatboxMetadata)
        : '';
    },
  },
  methods: {
    resetChatBox() {
      this.messages = [{ text: this.message, isUser: false }];
      this.interactions = [];
      this.chatPreviousMessage = null;
      this.summaryAlreadyRequested = false;
      this.chatId = uuidv4();
      this.initializeDialogPosition();

      this.sendRequest();
    },
    regenerateSummary() {
      this.sendRequest(true);
    },
    setupChannelSubscription() {
      this.channel.subscribe({
        next: (data) => {
          if (data.event.provided_id === this.chatId) {
            this.loadingProgress = data.event.progress_level;
            if (data.event.result) {
              setTimeout(() => {
                const result = this.strategy.onMessageReceived(data.event);

                if (result.isError) {
                  this.addSystemMessage(result.text);
                } else {
                  if (!this.isVisible) {
                    this.$buefy.toast.open({
                      duration: 5000,
                      message: 'Summary generated!',
                      type: 'is-success',
                      position: 'is-bottom',
                    });
                    this.forcePopup = true;
                  }

                  this.messages.push({
                    text: result.text,
                    isUser: false,
                    canBeCopied: true,
                  });

                  this.chatPreviousMessage = result.text;
                  this.toggleLoading(false);
                }
                this.loadingProgress = 0;
              }, 1500);
            }
          }
        },
        error: (err) => console.error('Subscription error:', err),
      });
    },
    toggleLoading(state) {
      this.showLoading = state;
    },
    getCurrentTimestamp() {
      const now = new Date();
      return `${now.getHours().toString().padStart(2, '0')}h:${now
        .getMinutes()
        .toString()
        .padStart(2, '0')}m:${now.getSeconds().toString().padStart(2, '0')}s`;
    },
    createInteraction(content = 'no user interaction content') {
      const sanitizeString = (value) =>
        typeof value === 'string' ? value.replace(/'/g, '') : value;

      return {
        interactionId: this.interactions.length + 1,
        timestamp: this.getCurrentTimestamp(),
        reply: {
          type: 'reply type',
          content: sanitizeString(this.chatPreviousMessage) || '',
        },
        userInteraction: {
          type: 'user interaction type',
          content,
        },
      };
    },
    createPayload(forceRegenerate) {
      if (forceRegenerate) {
        this.interactions = [];
      }

      return this.strategy.createPayload(
        this.chatId,
        this.chatboxMetadata,
        this.interactions
      );
    },

    async handleVisibilityChange() {
      this.initializeDialogPosition();

      const currentStrategyType = this.strategy?.type ?? null;
      const currentEntityIdentifier =
        this.strategy?.getIdentity(this.chatboxMetadata) ?? null;

      const storedSettings = this.getStoredStrategySettings();
      const storedStrategyType = storedSettings?.strategyType ?? null;
      const storedEntityIdentifier = storedSettings?.entityIdentifier ?? null;

      const strategyChanged = currentStrategyType !== storedStrategyType;
      const identityChanged =
        currentEntityIdentifier !== storedEntityIdentifier;

      if (strategyChanged || identityChanged) {
        this.resetChatBox();
        this.saveStrategySettings(currentStrategyType, currentEntityIdentifier);
        return;
      }

      if (!this.summaryAlreadyRequested) {
        await this.sendRequest();
      }
    },

    getStoredStrategySettings() {
      try {
        const raw = localStorage.getItem('chatBoxStrategy');
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn('Failed to parse stored strategy settings', e);
        return null;
      }
    },

    saveStrategySettings(type, identifier) {
      const settings = {
        strategyType: type,
        entityIdentifier: identifier,
      };
      localStorage.setItem('chatBoxStrategy', JSON.stringify(settings));
    },

    async sendRequest(forceRegenerate) {
      try {
        const payload = this.createPayload(forceRegenerate);

        await this.$store.dispatch(SEND_REPAIR_SELECT_REQUEST, payload);
        await this.$store.dispatch(REPAIR_SELECT_REQUEST, this.chatboxMetadata);

        this.summaryAlreadyRequested = true;
        this.toggleLoading(true);

        this.loadingProgress = 0.05;
      } catch (error) {
        this.toggleLoading(false);
        console.error('Error sending message:', error);
        this.addSystemMessage('An error occurred while processing the data');
      }
    },
    closeChatBox() {
      this.$emit('close');
      this.forcePopup = false;
    },
    initializeDialogPosition() {
      const saved = sessionStorage.getItem('dialogState');
      if (saved) {
        const state = JSON.parse(saved);
        this.position = state.position || {
          x: window.innerWidth - 520,
          y: window.innerHeight - 720,
        };
        this.width = state.width || 480;
        this.height = state.height || 600;
      } else {
        this.position = {
          x: window.innerWidth - 520,
          y: window.innerHeight - 720,
        };
        this.width = 480;
        this.height = 600;
      }
    },
    startDrag(event) {
      if (event.target.closest('.drag-icon')) {
        this.isDragging = true;
      }
    },
    stopDrag() {
      this.isDragging = false;
    },
    onDragStop(left, top) {
      this.position = { x: left, y: top };

      sessionStorage.setItem(
        'dialogState',
        JSON.stringify({
          position: this.position,
          width: this.width,
          height: this.height,
        })
      );
    },
    onResizeStop(left, top, width, height) {
      this.position = { x: left, y: top };
      this.width = width;
      this.height = height;

      sessionStorage.setItem(
        'dialogState',
        JSON.stringify({
          position: this.position,
          width: this.width,
          height: this.height,
        })
      );
    },
    addMessage(newMessage) {
      console.log(newMessage);
      this.messages.push({ text: newMessage, isUser: true });
      this.interactions.push(this.createInteraction(newMessage));
      this.parseMessage();
    },
    parseMessage() {
      const systemMsg = this.strategy.parseUserMessage();
      this.addSystemMessage(systemMsg);
      setTimeout(() => this.sendRequest(), 1000);
    },

    addSystemMessage(text) {
      this.messages.push({ text, isUser: false });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
}
.fade-enter-to {
  opacity: 1;
}
.fade-leave-from {
  opacity: 1;
}
.fade-leave-to {
  opacity: 0;
}

.dialog-container {
  width: 100%;
  height: 100%;
}

.dialog {
  width: 100%;
  height: 100%;
  background-color: #f7f7f7;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #ccc;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 1000;
  pointer-events: auto;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.18), 0 0 10px rgba(91, 62, 165, 0.3);
  border-radius: 12px;
}

.dialog ::-webkit-scrollbar {
  width: 6px;
}

.dialog ::-webkit-scrollbar-thumb {
  background-color: #5b3ea5;
  border-radius: 3px;
}

.fade-enter-active {
  transition: opacity 0.6s ease;
}

.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translate3d(0, 0, 0) scale(0.98);
}
.fade-enter-to,
.fade-leave-from {
  transform: translate3d(0, 0, 0) scale(1);
}
</style>
